import React from 'react';
import PropTypes from 'prop-types';
import { Button } from './Button';
import { CancelConfirmModal } from './CancelConfirmModal';
import { i18n } from '../i18n/translate';

export class Wizard extends React.Component {
  static propTypes = {
    allowCancel: PropTypes.bool,
    cancelButtonLabel: PropTypes.string,
    children: PropTypes.any,
    className: PropTypes.string,
    containerSelector: PropTypes.string,
    disableNextButton: PropTypes.bool,
    finalButtonDataAction: PropTypes.string,
    finalButtonLabel: PropTypes.string,
    hasButtons: PropTypes.bool,
    hasStepIndicator: PropTypes.bool,
    initialStep: PropTypes.number,
    nextButtonLabel: PropTypes.string,
    onCancel: PropTypes.func,
    onComplete: PropTypes.func,
    overrideSteps: PropTypes.bool,
    previousButtonLabel: PropTypes.string,
    wizardStep: PropTypes.number,
  };

  static defaultProps = {
    allowCancel: true,
    hasButtons: true,
    hasStepIndicator: true,
    overrideSteps: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      wizardStep: this.props.initialStep || 0,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.overrideSteps && props.wizardStep !== state.wizardStep) {
      return {
        wizardStep: props.wizardStep,
      };
    }
    return null;
  }

  buildStepWizard = () => {
    this.childSteps = [];

    let currentStep;
    React.Children.forEach(this.props.children, (child, i) => {
      if (i === this.state.wizardStep) {
        currentStep = React.cloneElement(child, {
          key: this.state.wizardStep,
          nextStep: this.nextStep.bind(this),
          previousStep: this.previousStep.bind(this),
          ref: step => {
            this.childSteps[i] = step;
          },
        });
      }
    });

    return currentStep;
  };

  buildStepIndicator = () => {
    const numChildren = React.Children.count(this.props.children);
    let step = 0;
    return React.Children.map(this.props.children, (child, i) => {
      if (child.props.title) {
        step++;
        return (
          <div className="stepindicator-child">
            <div className={i <= this.state.wizardStep ? 'circle complete' : 'circle'}>
              <div>{step}</div>
            </div>
            <div className="stepindicatorchild-label">{child.props.title}</div>
            {numChildren - 1 > i ? (
              <div className="separator-bar">
                <div />
              </div>
            ) : (
              ''
            )}
          </div>
        );
      }
    });
  };

  nextStep = async () => {
    this.navigateStep('goForward');
  };

  previousStep = async () => {
    this.navigateStep('goBackward');
  };

  navigateStep = async direction => {
    const currentStep = this.childSteps[this.state.wizardStep];
    this.setState({ disableButtons: true });
    try {
      const success = await currentStep[direction]();
      if (success) {
        if (direction === 'goBackward' && this.state.wizardStep === 0) {
          this.onCancel();
        } else if (
          direction === 'goForward' &&
          this.state.wizardStep === React.Children.count(this.props.children) - 1
        ) {
          if (this.props.onComplete) {
            this.props.onComplete();
          } else {
            this.onCancelClose(true);
          }
          this.setState({ disableButtons: false });
        } else {
          const container = this.props.containerSelector
            ? document.querySelector(`.${this.props.containerSelector}`)
            : window;
          container.scrollTo(0, 0);
          const newStep = direction === 'goBackward' ? this.state.wizardStep - 1 : this.state.wizardStep + 1;
          this.setState({ wizardStep: newStep, disableButtons: false });
        }
      } else {
        this.setState({ disableButtons: false });
      }
    } catch (e) {
      this.setState({ disableButtons: false });
    }
  };

  onCancel = () => {
    this.setState({ showCancelConfirm: true });
  };

  onCancelClose = close => {
    this.setState({ showCancelConfirm: false });
    if (close) {
      this.props.onCancel();
    } else {
      this.setState({ disableButtons: false });
    }
  };

  isFinalStep = () => {
    return this.state.wizardStep === React.Children.count(this.props.children) - 1;
  };

  render() {
    const {
      cancelButtonLabel = i18n.t('Cancel'),
      finalButtonLabel = i18n.t('Done'),
      finalButtonDataAction = 'final-step',
      hasButtons,
      hasStepIndicator,
      nextButtonLabel = i18n.t('Next'),
      previousButtonLabel = i18n.t('Back'),
    } = this.props;
    const lastButtonLabel = this.isFinalStep() ? finalButtonLabel : nextButtonLabel;
    const lastButtonDataAction = this.isFinalStep() ? finalButtonDataAction : 'next-step';

    return (
      <div className="wizard">
        {hasStepIndicator && React.Children.count(this.props.children) > 1 && (
          <div className="step-indicator">{this.buildStepIndicator()}</div>
        )}
        <div className="step-container">{this.buildStepWizard()}</div>
        {hasButtons && (
          <div className="wizard-buttons">
            <div className="wizard-buttons-left">
              {this.state.wizardStep > 0 && (
                <Button
                  data-action="previous-step"
                  icon="angle-left"
                  label={<span>{previousButtonLabel}</span>}
                  onClick={this.previousStep}
                  style="secondary"
                />
              )}
            </div>
            <div className="wizard-buttons-right">
              {this.props.allowCancel && (
                <Button
                  data-action="cancel-wizard"
                  label={<span>{cancelButtonLabel}</span>}
                  onClick={this.onCancel}
                  style="secondary"
                  className="m-r-5"
                />
              )}
              <Button
                data-action={lastButtonDataAction}
                disabled={this.props.disableNextButton || !!this.state.disableButtons}
                label={<span>{lastButtonLabel}</span>}
                onClick={this.nextStep}
                rightIcon="angle-right"
                style="primary"
              />
            </div>
          </div>
        )}
        <CancelConfirmModal show={this.state.showCancelConfirm} onCancelClose={this.onCancelClose} />
      </div>
    );
  }
}
